import { type RefObject, useEffect, useRef } from 'react';

export type UseInfiniteScrollOptions = {
  /**
   * Whether there is more data to load
   */
  hasMore?: boolean;
  /**
   * Whether data is currently being loaded
   */
  isLoading?: boolean;
  /**
   * Intersection observer threshold (0-1)
   * @default 0.1
   */
  threshold?: number;
  /**
   * Scrollable container to use as the intersection root. Fixes the case where
   * the list lives inside a CSS-transformed container (e.g. a modal) and the
   * default viewport root produces incorrect results.
   */
  root?: RefObject<Element | null>;
};

/**
 * Hook for implementing infinite scroll functionality using IntersectionObserver.
 * Triggers the callback when the ref element enters the viewport.
 *
 * @param callback - Function to call when the element intersects (e.g., load more data)
 * @param options - Configuration options
 * @returns Object containing the ref to attach to the sentinel element
 *
 * @example
 * ```tsx
 * const { loadMoreRef } = useInfiniteScroll(loadMore, {
 *   hasMore: pageInfo?.hasNextPage,
 *   isLoading: isFetchingMore,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     <div ref={loadMoreRef} />
 *   </div>
 * );
 * ```
 */
export const useInfiniteScroll = (
  callback: () => void,
  options: UseInfiniteScrollOptions = {},
) => {
  const { hasMore = true, isLoading = false, threshold = 0.1, root } = options;
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) {
      return;
    }

    // Don't observe if no more data or currently loading
    if (!hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          callback();
        }
      },
      {
        threshold,
        root: root?.current ?? null,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [callback, hasMore, isLoading, threshold, root]);

  return { loadMoreRef };
};
